import type { ColumnDef } from "@tanstack/react-table";

import type { BaseRowItem } from "../../../components/data-table";
import type { AdminSignupUser } from "../../../lib/api/adminSignups";
import { formatOrdinalDateTime } from "../clientsTable";

export const SIGNUPS_TABLE_LIMIT = 25;

function str(v: unknown): string {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

function fullName(u: AdminSignupUser): string {
  return [u.profile?.firstName, u.profile?.lastName]
    .map(str)
    .filter(Boolean)
    .join(" ");
}

function titleCase(v: string): string {
  return v ? v.charAt(0) + v.slice(1).toLowerCase() : v;
}

export type SignupRow = BaseRowItem & {
  raw: AdminSignupUser;
  name: string;
  email: string;
  emailVerified: boolean;
  phone: string;
  kycStatus: string;
  accountStatus: string;
  referralCode: string;
  signedUp: string;
};

export function buildSignupRows(users: AdminSignupUser[]): SignupRow[] {
  return users.map((u, index) => ({
    id: str(u._id) || `signup-${index}`,
    raw: u,
    name: fullName(u) || "—",
    email: str(u.email?.address) || "—",
    emailVerified: Boolean(u.email?.isVerified),
    phone: str(u.phone?.number) || "Not added",
    kycStatus: str(u.kyc?.status).toUpperCase() || "NONE",
    accountStatus: str(u.account?.status).toUpperCase() || "—",
    referralCode: str(u.referralCode) || "—",
    signedUp: formatOrdinalDateTime(u.createdAt),
  }));
}

function kycBadgeClass(status: string): string {
  switch (status) {
    case "VERIFIED":
      return "bg-emerald-500/15 text-emerald-400 border-emerald-500/35";
    case "PENDING":
      return "bg-amber-500/15 text-amber-300 border-amber-500/35";
    case "DENIED":
    case "FAILED":
      return "bg-red-500/15 text-red-300 border-red-500/35";
    default:
      return "bg-[#2C363E] text-[#AEBCC6] border-[#3A4852]";
  }
}

function accountBadgeClass(status: string): string {
  return status === "ACTIVE"
    ? "text-emerald-400"
    : status === "—"
    ? "text-[#869AA9]"
    : "text-amber-300";
}

export function createSignupColumns(
  onOpen: (row: SignupRow) => void
): ColumnDef<SignupRow, unknown>[] {
  return [
    {
      accessorKey: "name",
      header: "Name",
      cell: ({ row }) => (
        <div className="w-[200px]" title={row.original.name}>
          <p className="truncate capitalize text-[14px] text-white">
            {row.original.name}
          </p>
          <p className="mt-0.5 truncate text-[12px] text-[#869AA9] sm:hidden">
            {row.original.email}
          </p>
        </div>
      ),
    },
    {
      accessorKey: "email",
      header: "Email",
      meta: { hideBelow: "sm" as const },
      cell: ({ row }) => (
        <div className="flex max-w-[280px] items-center gap-2">
          <span
            className="truncate text-[13px] text-[#AEBCC6]"
            title={row.original.email}
          >
            {row.original.email}
          </span>
          <span
            className={`h-1.5 w-1.5 shrink-0 rounded-full ${
              row.original.emailVerified ? "bg-emerald-400" : "bg-[#3A4852]"
            }`}
            title={
              row.original.emailVerified
                ? "Email verified"
                : "Email not verified"
            }
            aria-label={
              row.original.emailVerified
                ? "Email verified"
                : "Email not verified"
            }
          />
        </div>
      ),
    },
    {
      accessorKey: "kycStatus",
      header: "KYC",
      cell: ({ row }) => (
        <span
          className={`inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-semibold tracking-wide ${kycBadgeClass(
            row.original.kycStatus
          )}`}
        >
          {titleCase(row.original.kycStatus)}
        </span>
      ),
    },
    {
      accessorKey: "phone",
      header: "Phone",
      meta: { hideBelow: "md" as const },
      cell: ({ getValue }) => (
        <span className="text-[13px] text-[#E7EBEE]">{String(getValue())}</span>
      ),
    },
    {
      accessorKey: "accountStatus",
      header: "Account",
      meta: { hideBelow: "lg" as const },
      cell: ({ row }) => (
        <span
          className={`text-[12px] font-semibold ${accountBadgeClass(
            row.original.accountStatus
          )}`}
        >
          {titleCase(row.original.accountStatus)}
        </span>
      ),
    },
    {
      accessorKey: "referralCode",
      header: "Referral code",
      meta: { hideBelow: "lg" as const },
      cell: ({ getValue }) => (
        <span className="font-mono text-[12px] text-[#AEBCC6]">
          {String(getValue())}
        </span>
      ),
    },
    {
      accessorKey: "signedUp",
      header: "Signed up",
      meta: { hideBelow: "md" as const },
      cell: ({ getValue }) => (
        <span className="text-[12px] text-[#869AA9]">{String(getValue())}</span>
      ),
    },
    {
      id: "actions",
      header: "",
      meta: { align: "right" as const },
      cell: ({ row }) => (
        <button
          type="button"
          className="rounded-lg border border-[#3A4852] bg-[#1C2227] px-3 py-1.5 text-[12px] font-semibold text-secondary_button transition hover:border-secondary_button hover:bg-[#273037]"
          onClick={() => onOpen(row.original)}
        >
          View
        </button>
      ),
    },
  ];
}

function csvCell(value: unknown): string {
  const s = value == null ? "" : String(value);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function buildSignupsCsv(users: AdminSignupUser[]): string {
  const header = [
    "First name",
    "Last name",
    "Email",
    "Email verified",
    "Phone",
    "KYC status",
    "Account status",
    "Referral code",
    "Signed up",
  ];
  const rows = users.map((u) =>
    [
      str(u.profile?.firstName),
      str(u.profile?.lastName),
      str(u.email?.address),
      u.email?.isVerified ? "Yes" : "No",
      str(u.phone?.number),
      str(u.kyc?.status) || "NONE",
      str(u.account?.status),
      str(u.referralCode),
      formatOrdinalDateTime(u.createdAt),
    ]
      .map(csvCell)
      .join(",")
  );
  return [header.join(","), ...rows].join("\n");
}
